import { Controller } from "stimulus"

export default class extends Controller {
  static targets = ["stage", "player", "video", "button", "overlay"]

  connect() {
    this.expanded = false
    this.started = false
    this.videoTarget.removeAttribute("autoplay")
    this.videoTarget.autoplay = false
    this.prepareStill()
    this.onPlayback = () => {
      if (!this.videoTarget.paused) this.markStarted()
      this.playerTarget.classList.toggle("is-paused", this.videoTarget.paused)
    }
    this.onKeydown = (event) => {
      if (event.key !== "Escape") return
      event.preventDefault()
      this.collapse(true)
    }
    this.onBackdrop = (event) => {
      if (event.target === this.stageTarget) this.collapse(true)
    }

    this.videoTarget.addEventListener("play", this.onPlayback)
    this.videoTarget.addEventListener("pause", this.onPlayback)
    this.onPlayback()
  }

  prepareStill() {
    const video = this.videoTarget
    this.lockRatio = () => {
      const width = video.videoWidth
      const height = video.videoHeight
      if (!width || !height) return

      const ratio = `${width} / ${height}`
      video.style.aspectRatio = ratio
      this.playerTarget.style.aspectRatio = ratio
      const frame = video.closest(".drill-animation-frame")
      if (frame) frame.style.aspectRatio = ratio
    }
    this.showFrame = () => {
      this.lockRatio()
      if (this.started || this.frameReady || video.readyState < 2) return

      this.frameReady = true
      if (video.currentTime > 0.05) return

      const duration = Number.isFinite(video.duration) ? video.duration : 0
      const nudge = duration > 0 ? Math.min(0.04, duration / 2) : 0.04
      if (nudge <= 0) return

      video.addEventListener("seeked", () => {
        if (!this.started) video.pause()
      }, { once: true })
      try { video.currentTime = nudge } catch (error) {}
    }

    video.preload = "auto"
    video.removeAttribute("poster")
    if (!video.getAttribute("src") && video.dataset.src) video.src = video.dataset.src
    video.addEventListener("loadedmetadata", this.lockRatio)
    video.addEventListener("loadeddata", this.showFrame)
    if (video.readyState >= 1) this.lockRatio()
    if (video.readyState >= 2) this.showFrame()
  }

  disconnect() {
    this.collapse()
    if (this.hasVideoTarget) {
      this.videoTarget.removeEventListener("play", this.onPlayback)
      this.videoTarget.removeEventListener("pause", this.onPlayback)
      this.videoTarget.removeEventListener("loadedmetadata", this.lockRatio)
      this.videoTarget.removeEventListener("loadeddata", this.showFrame)
    }
  }

  play(event) {
    event.preventDefault()
    event.stopPropagation()

    const video = this.videoTarget
    if (!video.getAttribute("src") && video.dataset.src) video.src = video.dataset.src
    delete video.dataset.userPaused

    const playPromise = video.play()
    if (playPromise && playPromise.catch) playPromise.catch(() => {})
  }

  toggle(event) {
    event.preventDefault()
    event.stopPropagation()
    if (this.expanded) {
      this.collapse()
    } else {
      this.expand()
    }
  }

  markStarted() {
    if (this.started) return

    this.started = true
    this.videoTarget.dataset.chapterVideoStarted = "true"
    this.playerTarget.classList.add("is-started")
    if (this.hasOverlayTarget) this.overlayTarget.hidden = true

    const frame = this.videoTarget.closest(".drill-animation-frame")
    if (!frame) return

    frame.setAttribute("tabindex", "0")
    frame.removeAttribute("aria-hidden")
  }

  expand() {
    if (this.expanded) return

    this.hold = this.stageTarget.parentElement
    this.hold.style.minHeight = `${this.stageTarget.offsetHeight}px`
    this.stageTarget.classList.add("is-expanded")
    document.body.classList.add("editorjs-video-expanded")
    this.stageTarget.addEventListener("click", this.onBackdrop)
    document.addEventListener("keydown", this.onKeydown)
    this.expanded = true
    this.updateButton()
  }

  collapse(restoreFocus = false) {
    if (!this.expanded) return

    this.stageTarget.classList.remove("is-expanded")
    if (this.hold) this.hold.style.minHeight = ""
    document.body.classList.remove("editorjs-video-expanded")
    this.stageTarget.removeEventListener("click", this.onBackdrop)
    document.removeEventListener("keydown", this.onKeydown)
    this.expanded = false
    this.updateButton()
    if (restoreFocus) this.buttonTarget.focus()
  }

  updateButton() {
    const label = this.expanded ? this.buttonTarget.dataset.shrinkLabel : this.buttonTarget.dataset.expandLabel
    this.buttonTarget.querySelector("[data-expand-icon]").hidden = this.expanded
    this.buttonTarget.querySelector("[data-shrink-icon]").hidden = !this.expanded
    this.buttonTarget.setAttribute("aria-label", label)
    this.buttonTarget.setAttribute("title", label)
    this.buttonTarget.setAttribute("aria-expanded", this.expanded ? "true" : "false")
  }
}
